import { useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "../context/auth-context";
import { pbkdf2 } from "pbkdf2";
import Cookies from "js-cookie";
import { CONFIG } from "./config";

function pbkdf2Async(password, salt, iterations, keylen, digest) {
  return new Promise((res, rej) => {
    pbkdf2(password, salt, iterations, keylen, digest, (err, key) => {
      err ? rej(err) : res(key);
    });
  });
}

export function useIpDetails() {
  return useQuery(
    ["ip-details"],
    () =>
      fetch("https://pro.ip-api.com/json?key=3pQZfl587vsCBcB").then(
        async (res) => {
          const data = await res.json();
          if (!res.ok) {
            return Promise.reject(data);
          }
          return data;
        },
      ),
    {
      initialData: undefined,
      staleTime: Infinity,
      refetchOnMount: false,
      retry: false,
      retryOnMount: false,
    },
  );
}

export function formatUrlName(name) {
  return name
    .trim() // Remove leading and trailing spaces
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .toLowerCase(); // Convert to lowercase
}

export const formatName = (name) => {
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

export async function generateHash(dataToHash) {
  const salt = CONFIG.hashKey || "";
  try {
    const hash = await pbkdf2Async(
      JSON.stringify(dataToHash),
      salt,
      10000,
      100,
      "sha512",
    );
    return hash.toString("hex");
  } catch (error) {
    return "";
  }
}

export function makeid(length) {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

export async function getTokenServerSide(identifier) {
  return await fetch("https://pro.ip-api.com/json?key=3pQZfl587vsCBcB").then(
    async (res) => {
      const data = await res.json();
      if (!res.ok) {
        return Promise.reject(data);
      }
      return {
        token: (await getAccessServerSideToken(data, identifier))?.data || null,
        identifier,
        ipDetails: data,
      };
    },
  );
}

export async function getNewToken(identifier) {
  let getToken = null;
  try {
    getToken = await getTokenServerSide(identifier?.value || makeid(8));
  } catch (err) {
    return null;
  }

  if (!getToken) {
    return null;
  }

  if (!getToken.token) return null;

  const { token: newToken } = getToken;
  return newToken.token;
}

function generateUniqueBrowserIdentifier() {
  const randomPart = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(
    /[018]/g,
    (c) =>
      (
        c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
      ).toString(16),
  );
  const timestamp = Date.now();
  return `${randomPart}-${timestamp}`;
}

export function getIdentifier() {
  let identifier = localStorage.getItem("identifier");
  if (!identifier) {
    identifier = generateUniqueBrowserIdentifier();
    localStorage.setItem("identifier", identifier);
  }
  Cookies.set("identifier", identifier, {});
  return identifier;
}

function removeNonAlphanumericExceptAmpersand(str) {
  return str.replace(/[^a-zA-Z0-9& ]/g, "");
}

export function convertTitleToURLPath(title) {
  title = removeNonAlphanumericExceptAmpersand(title);
  // Remove leading and trailing spaces (if any)
  const trimmedTitle = title.trim();
  // Replace spaces with dashes to create the URL path
  const urlPath = trimmedTitle.replace(/\s+/g, "-");

  return urlPath;
}

export function daysToReachTargetDate(targetDate) {
  // Get the current date
  const currentDate = new Date();

  // Convert the target date string to a Date object
  const targetDateObj = new Date(targetDate);

  // Calculate the time difference in milliseconds
  const timeDifference = targetDateObj - currentDate;

  // Convert milliseconds to days
  const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  return daysDifference;
}

export async function getAccessServerSideToken(metaData, identifier) {
  identifier = !identifier ? getIdentifier() : identifier;
  const hash = await generateHash({
    identifier,
    tokenType: "guest",
  });
  return fetch(CONFIG.apiUrl + "/access/token", {
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
    method: "POST",
    body: JSON.stringify({
      accessLevel: "regular",
      identifier,
      hash,
      ...generateMetaDataServerSide(metaData),
    }),
  })
    .then(async (i) => {
      let j = await i.json();
      return j;
    })
    .catch((e) => {
      console.log("server side token failed", { e });
    });
}

export async function getAccessToken(metaData) {
  const identifier = getIdentifier();
  const hash = await generateHash({
    identifier,
    tokenType: "guest",
  });
  const requestBody = JSON.stringify({
    identifier,
    accessLevel: "regular",
    hash,
    ...generateMetaData(metaData),
  });
  try {
    const response = await fetch(CONFIG.apiUrl + "/access/token", {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      method: "POST",
      body: requestBody,
    });
    const data = await response.json();
    if (!response.ok) {
      console.error("Error response from server:", data);
      return Promise.reject(data);
    }
    return data;
  } catch (error) {
    console.error("Failed to fetch in getAccessToken:", error);
    throw error;
  }
}

export async function decodeAccessToken(token) {
  const requestBody = JSON.stringify({
    token,
    app: "web",
  });
  try {
    const response = await fetch(CONFIG.apiUrl + "/access/decode", {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      method: "POST",
      body: requestBody,
    });
    const data = await response.json();
    if (!response.ok) {
      console.error("Error response from server:", data);
      return Promise.reject(data);
    }
    return data;
  } catch (error) {
    console.error("Failed to fetch in getAccessToken:", error);
    throw error;
  }
}

export function useAddToWaitlist({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();

  return useMutation(
    (email) =>
      client("contact/create", {
        data: {
          email,
          contactType: "WAITING_LIST",
          ...generateMetaData(data),
        },
      }),
    {
      ...config,
    },
  );
}

export function useContactFormList({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();

  return useMutation(
    (params) =>
      client("contact/create", {
        data: {
          ...params,
          contactType: "LETS_TALK",
          ...generateMetaData(data),
        },
      }),
    {
      ...config,
    },
  );
}

function generateMetaData(metaData) {
  const userAgent = window.navigator.userAgent;
  const os = getOS();
  return {
    app: "web",
    metaData: {
      ipAddress: metaData.query,
      country: metaData.country,
      countryCode: metaData.countryCode,
      userAgent: userAgent,
      latitude: metaData.lat,
      longitude: metaData.lon,
      city: metaData.regionName,
      timezone: metaData.timezone,
      deviceName: window.navigator.platform,
      os: os,
      deviceNo: "",
    },
  };
}
function generateMetaDataServerSide(metaData) {
  return {
    app: "web",
    metaData: {
      ipAddress: metaData?.query,
      country: metaData?.country,
      countryCode: metaData?.countryCode,
      userAgent: "ds",
      latitude: metaData?.lat,
      longitude: metaData?.lon,
      city: metaData?.regionName,
      timezone: metaData?.timezone,
      deviceName: "ds",
      os: "ANDROID",
      deviceNo: "",
    },
  };
}

function getOS() {
  var userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"],
    windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
    iosPlatforms = ["iPhone", "iPad", "iPod"],
    os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "MAC";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "IOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "WINDOWS";
  } else if (/Android/.test(userAgent)) {
    os = "ANDROID";
  } else if (!os && /Linux/.test(platform)) {
    os = "OTHERS";
  } else {
    os = null;
  }
  return os;
}

export const getInitials = function (name, maxCharter = null) {
  if (!name || typeof name !== "string") return "";

  // Split by spaces, trim, and remove empties
  const parts = name.trim().split(/\s+/).filter(Boolean);

  // Build initials from first character of each part
  let initials = parts
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  // Limit length if maxCharter is provided
  if (maxCharter && Number.isInteger(maxCharter) && maxCharter > 0) {
    initials = initials.slice(0, maxCharter);
  }

  return initials;
};

export const getBoxSize = (
  width,
  height,
  params,
  align = "r",
  target = 500,
) => {
  let { x_max_size, y_max_size, y_min_size, x_min_size } = params;

  let larger_dim = "x";
  let larger_curr_value = null;
  let larger_dim_value = null;

  let x_check = parseFloat(width) - parseFloat(x_max_size);
  let y_check = parseFloat(height) - parseFloat(y_max_size);

  if (x_check < y_check) {
    larger_dim = "y";
    larger_curr_value = parseFloat(height);
    larger_dim_value = parseFloat(y_max_size);
  } else {
    larger_dim = "x";
    larger_curr_value = parseFloat(width);
    larger_dim_value = parseFloat(x_max_size);
  }

  let tn_width = width;
  let tn_height = height;

  if (align == "r") {
    const target_size = parseInt(larger_dim_value);
    if (larger_curr_value > larger_dim_value) {
      if (larger_dim == "x") {
        tn_width = Math.floor(target_size);
        tn_height = Math.floor((height / width) * target_size);
      } else {
        tn_height = Math.floor(target_size);
        tn_width = Math.floor((width / height) * target_size);
      }
    } else {
      tn_width = width;
      tn_height = height;
    }
    if (y_min_size && x_min_size) {
      if (tn_height < y_min_size) {
        tn_height = Math.floor(y_min_size);
        tn_width = Math.floor((width / height) * y_min_size);
      }

      if (tn_width < x_min_size) {
        tn_width = Math.floor(x_min_size);
        tn_height = Math.floor((height / width) * x_min_size);
      }
    }
  } else if (align == "w") {
    tn_height = Math.floor((target * height) / width);
    tn_width = Math.floor(target);
  } else if (align == "h") {
    tn_width = Math.floor((target * width) / height);
    tn_height = Math.floor(target);
  }

  return {
    width: tn_width,
    height: tn_height,
  };
};

export function useCirclePlugLogin({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client("access/auth", {
        data: {
          ...params,
          accessLevel: "regular",
          ...generateMetaData(data),
        },
      }),
    {
      ...config,
    },
  );
}

export function useCirclePlugSignUp({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client("access/create", {
        data: {
          ...params,
          createType: "user",
          ...generateMetaData(data),
        },
      }),
    {
      ...config,
    },
  );
}

export function useFreeTicketRegister({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client(`event/${params.eventId}/user/${params.userId}/ticket/register`, {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      }),
    {
      ...config,
    },
  );
}

export function usePublicFreeTicketRegister({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client(`event/public/${params.eventId}/register`, {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      }),
    {
      ...config,
    },
  );
}

export function useAccessToken({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client("access/token", {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      }),
    {
      ...config,
    },
  );
}

export function usePaidTicketRegister({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const requestConfig = {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (params.token) {
        requestConfig.headers = {
          Authorization: `Bearer ${params.token}`,
        };
      }
      return client(`event/${params.eventId}/payment/init`, requestConfig);
    },
    {
      ...config,
    },
  );
}

export function useProcessCart({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const { orgId, accessPointId, token } = params;
      const url = `collection/organisation/${orgId}/cart/process`;
      const urlParams = new URLSearchParams();
      urlParams.append("accessPointId", accessPointId);
      const requestConfig = {
        method: "POST",
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      return client(`${url}?${urlParams.toString()}`, requestConfig);
    },
    {
      ...config,
    },
  );
}

export function useUpdateCart({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const { orgId, accessPointId, token } = params;
      const url = `collection/organisation/${orgId}/cart`;
      const urlParams = new URLSearchParams();
      urlParams.append("accessPointId", accessPointId);
      const requestConfig = {
        method: "POST",
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      return client(`${url}?${urlParams.toString()}`, requestConfig);
    },
    {
      ...config,
    },
  );
}

export function useCartPayment({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const requestConfig = {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (params.token) {
        requestConfig.headers = {
          Authorization: `Bearer ${params.token}`,
        };
      }
      return client(
        `collection/${params.collectionId}/payment/init`,
        requestConfig,
      );
    },
    {
      ...config,
    },
  );
}

export function useCartPaymentCharge({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const requestConfig = {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (params.token) {
        requestConfig.headers = {
          Authorization: `Bearer ${params.token}`,
        };
      }
      return client(
        `collection/${params.collectionId}/payment/charge`,
        requestConfig,
      );
    },
    {
      ...config,
    },
  );
}

export function useActiveCart({ ...config }) {
  const client = useClient();
  return useMutation(
    ({ orgId, accessPointId, token }) => {
      const url = `collection/organisation/${orgId}/cart`;
      const params = new URLSearchParams();
      params.append("accessPointId", accessPointId);
      const requestConfig = {
        method: "GET",
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      return client(`${url}?${params.toString()}`, requestConfig);
    },
    {
      ...config,
    },
  );
}

export function useCloseCart({ ...config }) {
  const client = useClient();
  return useMutation(
    ({ orgId, accessPointId, token }) => {
      const url = `collection/organisation/${orgId}/cart/close`;
      const params = new URLSearchParams();
      params.append("accessPointId", accessPointId);
      const requestConfig = {
        method: "GET",
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      return client(`${url}?${params.toString()}`, requestConfig);
    },
    {
      ...config,
    },
  );
}

export function useCartSignature({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const requestConfig = {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (params.token) {
        requestConfig.headers = {
          Authorization: `Bearer ${params.token}`,
        };
      }
      return client(
        `collection/${params.collectionId}/cart/signature`,
        requestConfig,
      );
    },
    {
      ...config,
    },
  );
}

export function useCartCancelPayment({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) => {
      const requestConfig = {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      };
      if (params.token) {
        requestConfig.headers = {
          Authorization: `Bearer ${params.token}`,
        };
      }
      return client(
        `collection/${params.collectionId}/cart/cancel`,
        requestConfig,
      );
    },
    {
      ...config,
    },
  );
}

export function useJoinCommunity({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client(`organisation/${params.orgId}/user/${params.userId}/join`, {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      }),
    {
      ...config,
    },
  );
}

export function useCheckUserMembership({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client(`organisation/${params.orgId}/user/${params.userId}/info`, {}),
    {
      ...config,
    },
  );
}

export function useGetEvent({ ...config }) {
  const client = useClient();
  return useMutation(
    (params) => client(`event/public/${params.eventId}/info`, {}),
    {
      ...config,
    },
  );
}

export function useGetPollCandidates({ ...config }) {
  const client = useClient();
  return useMutation(
    ({ pollId, pollQuestionId, searchTerm, token }) => {
      const url = `poll/${pollId}/candidates/feeds`;
      // Manually add query parameters
      const params = new URLSearchParams();
      if (pollQuestionId) params.append("pollQuestionId", pollQuestionId);
      if (searchTerm) params.append("searchTerm", searchTerm);
      const requestConfig = {};
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      return client(`${url}?${params.toString()}`, requestConfig);
    },
    {
      ...config,
    },
  );
}

export function useGetProduct({ ...config }) {
  const client = useClient();
  return useMutation(
    (params) =>
      client(
        `product/${params.productId}/info?useFeed=true&accessPointId=${params.accessPointId}`,
        {},
      ),
    {
      ...config,
    },
  );
}

export function useGetSeeAllCandidates({ ...config }) {
  const client = useClient();
  return useMutation(
    (params) => client(`poll/${params.pollId}/questions/feeds?limit=${10}`, {}),
    {
      ...config,
    },
  );
}

export function useGetPollCandidatesOptions({ ...config }) {
  const client = useClient();
  return useMutation(
    (params) =>
      client(
        `poll/${params.pollId}/question/${params.pollQuestionId}/candidates/feeds`,
        {},
      ),
    {
      ...config,
    },
  );
}

export function useVerifyOtp({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client("access/otp/verify", {
        data: {
          ...params,
          ...generateMetaData(data),
        },
        headers: {
          Authorization: `Bearer ${params.token}`,
        },
      }),
    {
      ...config,
    },
  );
}

export function useInitOtp({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();

  return useMutation(
    (params) =>
      client("access/otp/init", {
        data: {
          ...params,
          ...generateMetaData(data),
        },
        ...(params.token && {
          headers: {
            Authorization: `Bearer ${params.token}`,
          },
        }),
      }),
    {
      ...config,
    },
  );
}

export function useResetPassword({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client("access/password/change", {
        data: {
          ...params,
          type: "single",
          ...generateMetaData(data),
        },
        headers: {
          Authorization: `Bearer ${params.token}`,
        },
      }),
    {
      ...config,
    },
  );
}

export function useGetDestinations({ ...config }) {
  const client = useClient();

  return useMutation(
    ({ orgId, accessPointId, token }) => {
      const url = `geo/organisation/${orgId}/point/${accessPointId}/destinations`;
      const requestConfig = {
        method: "GET",
      };

      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }

      return client(url, requestConfig);
    },
    {
      ...config,
    },
  );
}

export function useFetchAccessPoint({ ...config }) {
  const client = useClient();
  return useMutation(
    ({ orgId, token }) => {
      const url = `crowdcontrol/organisation/${orgId}/accesspoints/inventory`;
      const requestConfig = {
        method: "GET",
      };
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }
      return client(url, requestConfig);
    },
    { ...config },
  );
}
export function useFetchMainAccessPoint({ ...config }) {
  const client = useClient();
  return useMutation(
    ({ orgId, token }) => {
      const url = `crowdcontrol/organisation/${orgId}/point/main`;
      const requestConfig = {
        method: "GET",
      };

      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }

      return client(url, requestConfig);
    },
    { ...config },
  );
}

export function useFetchOrganisation({ ...config }) {
  const client = useClient();
  return useMutation(
    ({ orgId, token }) => {
      const url = `organisation/${orgId}/info`;
      const requestConfig = {
        method: "GET",
      };

      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }

      return client(url, requestConfig);
    },
    { ...config },
  );
}

export function useFetchUserCollections({
  userId,
  token,
  enabled = false,
  onSuccess,
  onError,
} = {}) {
  const client = useClient();

  // Key depends on userId + token so cache is properly segmented per user/session
  const queryKey = ["userCollections", userId, token];

  const queryFn = async () => {
    if (!userId) throw new Error("Missing userId");
    const url = `collection/user/${userId}/carts`;
    const requestConfig = {
      method: "GET",
    };

    if (token) {
      requestConfig.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    const res = await client(url, requestConfig);
    return res;
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!enabled && !!userId,
    onSuccess,
    onError,
  });
}
export function useFetchGuestCollections({
  token,
  enabled = false,
  onSuccess,
  onError,
} = {}) {
  const client = useClient();

  const queryKey = ["guestCollections", token];

  const queryFn = async () => {
    const url = `collection/carts`;
    const requestConfig = {
      method: "GET",
    };

    if (token) {
      requestConfig.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    const res = await client(url, requestConfig);
    return res;
  };

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!enabled,
    onSuccess,
    onError,
  });
}

export function generateAbbreviation(name = "") {
  const words = name.split(/\s+/).filter(Boolean);
  const letters = [];

  for (const word of words) {
    // Find first alphabetical character in the word
    const match = word.match(/[A-Za-z]/);
    if (match) {
      letters.push(match[0].toUpperCase());
    }

    // Stop once we have 2 letters
    if (letters.length === 2) break;
  }

  return letters.join("");
}

export function extractInfoFromUrl(query) {
  const search = window.location.search;

  const params = new URLSearchParams(search);

  const value = params.get(query);

  return value;
}

export async function sendToSlack(message) {
  return fetch(CONFIG.apiUrl + "/webhook/slack", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(message),
  })
    .then(async (i) => {
      let j = await i.json();
      return j;
    })
    .catch((e) => {
      console.log("server side token failed", { e });
    });
}

export const getStorage = (storageKey) => {
  try {
    if (typeof window === "undefined") return null;
    const item = localStorage.getItem(storageKey);
    if (item) {
      return JSON.parse(item);
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

export const setStorage = (storageKey, data) => {
  if (typeof window === "undefined") return null;
  try {
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.error(error);
  }
};

export const unsetStorage = (storageKey) => {
  if (typeof window === "undefined") return null;
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error(error);
  }
};

export const clearStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.clear();
  } catch (error) {
    console.error(error);
  }
};

export const setLoggedinUser = (user) => {
  return setStorage("__user", user);
};

export const getLoggedinUser = () => {
  return getStorage("__user");
};

export function useUploadMedia({ ...config }) {
  return useMutation(
    async ({ file, token, folder }) => {
      const formData = new FormData();
      formData.append("media", file);

      const uploadMedia = await uploadToMedia(token, file, folder);

      if (!uploadMedia) {
        throw new Error("File upload failed");
      }

      return uploadMedia;
    },
    {
      ...config,
    },
  );
}

export const formatChatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }); // DD/MM/YYYY
};

export const normalizeName = (str = "") => {
  return decodeURIComponent(str || "") // turn %20 into spaces
    .toLowerCase() // lowercase everything
    .replace(/\s+/g, "-") // spaces → hyphens
    .replace(/[^a-z0-9\-']/g, "") // ❗ allow apostrophes
    .replace(/-+/g, "-") // collapse multiple hyphens
    .replace(/^-|-$/g, ""); // remove hyphens at ends
};

export async function uploadScreenshotBase64({
  base64,
  token,
  folder = "errors",
}) {
  const blob = await (await fetch(base64)).blob();
  const file = new File([blob], `error-${Date.now()}.png`, {
    type: "image/png",
  });

  const uploadMedia = await uploadToMedia(token, file, "errors");

  if (!uploadMedia) {
    throw new Error("Screenshot upload failed");
  }

  if (uploadMedia.error) {
    throw new Error(uploadMedia.message);
  }

  return uploadMedia.response.media.thumb.replace("r200x200", "r1000x1000");
}

export async function uploadToMedia(token, file, folder) {
  const formData = new FormData();
  formData.append("media", file);

  const response = await fetch(`${CONFIG.mediaUrl}/upload/${folder}`, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) false;
  return await response.json();
}
export async function postComment(payload, sectionId, sectionName, token) {
  const response = await fetch(
    `${CONFIG.apiUrl}/conversation/${sectionName}/${sectionId}/comment`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json", // 👈 add this
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) false;
  return await response.json();
}
export const deleteComment = async (trackId, token) => {
  const res = await fetch(`${CONFIG.apiUrl}/conversation/${trackId}/remove`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error("Failed to delete comment");
  }

  return res.json();
};

export const updateComment = async (trackId, payload, token) => {
  const res = await fetch(`${CONFIG.apiUrl}/conversation/${trackId}/edit`, {
    method: "PUT",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json", // 👈 add this
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error("Failed to update comment");
  }
  return res.json();
};

export const readReachOutBoxComments = async (sectionId, token) => {
  const res = await fetch(
    `${CONFIG.apiUrl}/conversation/reachout/${sectionId}/read`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json", // 👈 add this
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!res.ok) {
    throw new Error("Comments were not Read");
  }
  return res.json();
};

export const sendTypingEvent = async (
  action,
  user,
  sectionId,
  sectionName,
  token,
) => {
  try {
    const res = await fetch(`${CONFIG.socketServerUrl}/pushUpdate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        eventKey: `${sectionId}:${sectionName}`,
        eventData: {
          action,
          data: {
            firstName: `${user?.firstName}`,
            lastName: `${user?.lastName}`,
            id: `${user?.userId}`,
          },
        },
      }),
    });
    if (!res.ok) {
      throw new Error("Failed to update comment");
    }
  } catch (err) {
    console.error("Typing event error:", err);
  }
};

export async function verifyPassword(token, userId, formData, ipDetails) {
  try {
    const response = await fetch(
      `${CONFIG.apiUrl}/access/user/${userId}/password/verify`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          ...generateMetaData(ipDetails),
        }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData?.message);
    }
    return await response.json();
  } catch (error) {
    console.error("Error verifying password:", error);
    throw error;
  }
}

export async function updatePassword(token, userId, formData, ipDetails) {
  try {
    const response = await fetch(
      `${CONFIG.apiUrl}/access/user/${userId}/password`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          ...generateMetaData(ipDetails),
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData?.message);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating password:", error);
    throw error;
  }
}

export function useOrganisationCategories(orgId, token, accessPointId) {
  const client = useClient();
  return useQuery(
    ["categories", orgId, accessPointId],
    async () => {
      const queryParams = new URLSearchParams();
      queryParams.append("accessPointId", accessPointId);

      const queryString = queryParams.toString();
      const url = `product/category/organisation/${orgId}/feeds?includeProducts=true${
        queryString ? `&${queryString}` : ""
      }`;

      const res = await client(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return res.data;
    },
    {
      enabled: Boolean(orgId && token && accessPointId),
      keepPreviousData: true, // helpful for pagination UX
    },
  );
}

export function useGetAddressCoordinates({ ...config }) {
  const client = useClient();
  return useMutation(
    async ({ address }) => {
      const url = `geo/geocode?address=${address}`;
      const requestConfig = {
        method: "GET",
      };

      const res = await client(url, requestConfig);
      return res;
    },
    {
      ...config,
    },
  );
}

export const useCurrencies = (client, token) => {
  return useQuery(
    ["currencies"],
    async () => {
      const res = await client(`configuration/currency/currencies`);
      return res.data?.currencies || [];
    },
    {
      enabled: !!token,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      onError: (err) => {
        console.error("Failed to fetch currencies:", err);
      },
    },
  );
};

// export const getCurrencyFromUrl = () => {
//   const defaultCurrency = "NGN";

//   if (typeof window === "undefined") {
//     return {
//       code: defaultCurrency,
//       symbol: currencyMap[defaultCurrency]?.symbol || "₦",
//     };
//   }

//   const params = new URLSearchParams(window.location.search);

//   const currencyParam = params.get("currency");

//   const code = currencyParam ? currencyParam.toUpperCase() : defaultCurrency;

//   return {
//     code,
//     symbol: currencyMap[code]?.symbol || "₦",
//   };
// };

export const generateProductUrl = (storePath, product) => {
  // Clean + slugify product name
  const slug = product.name
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // remove special characters
    .replace(/\s+/g, "-") // spaces → dashes
    .replace(/-+/g, "-"); // collapse multiple dashes

  // Normalize storePath (remove trailing /)
  const base = storePath === "/" ? "" : storePath.replace(/\/+$/, "");

  return `${base}${base ? "/" : ""}product/${slug}-${product.checkSum}`;
};

export const knownPaths = [
  "auth",
  "product",
  "checkout",
  "profile",
  "home",
  "market-place",
  "stores",
];

export const knownMarketplacePaths = [
  "stores",
  "checkout",
  "auth",
  "home",
  "profile",
];

export const getUrlPathFromPathname = (pathname) => {
  const lowerKnownPaths = knownPaths.map((p) => p.toLowerCase());
  const normalized = pathname.toLowerCase();
  const parts = normalized.split("/").filter(Boolean);

  // ✅ Root path → home
  if (normalized === "/") {
    return "home";
  }

  // ✅ market-place + subpaths (checkout, orders, etc.)
  if (normalized.startsWith("/market-place")) {
    const after = parts.slice(1); // everything after "market-place"

    const match = after.find((seg) => lowerKnownPaths.includes(seg));
    if (match) {
      return match;
    }

    return "market-place";
  }

  // ✅ /store/:handle/... pattern
  const storeIndex = parts.findIndex((p) => p === "store");
  if (storeIndex !== -1 && parts.length > storeIndex + 1) {
    const afterHandle = parts.slice(storeIndex + 2); // skip "store" + handle

    if (afterHandle.length === 0) return "home";

    // find first known path in the rest
    const match = afterHandle.find((seg) => lowerKnownPaths.includes(seg));
    if (match) return match;

    return afterHandle.length === 1 ? "home" : "unknown";
  }

  // ✅ Subdomain style (/auth, /product, /lagos/product, etc.)
  if (parts.length > 0) {
    const match = parts.find((seg) => lowerKnownPaths.includes(seg));
    if (match) return match;

    // if only location → home
    if (parts.length === 1) return "home";
  }

  return "unknown";
};

const KNOWN_PATHS = new Set(knownPaths);

function cleanPath(pathname) {
  return pathname.split("?")[0].split("#")[0];
}

export function getStorePath(pathname) {
  const clean = cleanPath(pathname);
  const segments = clean.split("/").filter(Boolean);

  if (!segments.length) return "";

  // ✅ special-case marketplace
  if (segments[0] === "market-place") {
    return "market-place";
  }

  const storeSegments = [];

  for (const segment of segments) {
    if (KNOWN_PATHS.has(segment)) break;
    storeSegments.push(segment);
  }

  return storeSegments.join("/");
}

export function getBasePath(pathname) {
  const clean = cleanPath(pathname);
  const segments = clean.split("/").filter(Boolean);

  // market-place or anything under it
  if (segments[0] === "market-place") {
    return "market-place";
  }

  // store/<brand>/...
  if (segments[0] === "store" && segments[1]) {
    return "store/" + segments[1];
  }

  return "";
}

export function extractChecksumFromPath(pathname) {
  return pathname.split("/").pop()?.split("-").pop() || "";
}

export const buildQueryString = (params = {}, mainQuery = true) => {
  let finalParams = {};

  if (mainQuery && typeof window !== "undefined") {
    // Parse current query params
    const currentParams = new URLSearchParams(window.location.search);
    for (const [key, value] of currentParams.entries()) {
      finalParams[key] = value;
    }
  }

  // Merge or override with new params
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === "" || value === null) {
      // If you want to completely remove a param when value is empty, delete it
      delete finalParams[key];
    } else {
      finalParams[key] = value;
    }
  });

  // Build query string
  const query = Object.entries(finalParams)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");

  return query ? `?${query}` : "";
};

export const isTokenValid = (token, expiry) => {
  if (!token || !expiry) return false;
  const expiryDate = new Date(expiry);
  return expiryDate > new Date();
};

export async function getGeoLocation() {
  const API_KEY = "3pQZfl587vsCBcB";
  const endpoint = `https://pro.ip-api.com/json?key=${API_KEY}`;

  try {
    const response = await fetch(endpoint);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    if (data.status !== "success") {
      throw new Error(`API error: ${data.message || "Unknown error"}`);
    }

    return data; // IP, country, region, city, etc.
  } catch (error) {
    console.error("Failed to fetch geolocation:", error);
    return null;
  }
}

export const useAuth = () => {
  const logUserIn = async ({ user, token, tokenExpiryDate, sessionId }) => {
    const loggedInUser = {
      sessionId,
      tokenExpiryDate,
      token,
      user,
    };

    setLoggedinUser(loggedInUser);

    return {
      loggedInUser,
      authenticated: true,
      token,
    };
  };

  const logUserOut = async (metaData = null) => {
    unsetStorage("__user");
    let token = null;
    let tokenExpiryDate = null;
    try {
      if (!metaData) {
        metaData = await getGeoLocation();
      }
      const getPublicToken = await getAccessToken(metaData);
      if (getPublicToken?.data?.token) {
        token = getPublicToken.data.token;
        tokenExpiryDate = getPublicToken.data.tokenExpiryDate;
      }
    } catch (error) {
      console.error("Error fetching public token on logout:", error);
    }
    return {
      token,
      tokenExpiryDate,
    };
  };

  return {
    logUserIn,
    logUserOut,
  };
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function generateObjectId() {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  return (
    timestamp +
    "xxxxxxxxxxxxxxxx" // 16 random hex chars
      .replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16))
  ).toLowerCase();
}

const sanitizePathSegment = (value) => {
  if (!value || typeof value !== "string") return "";
  return value.replace(/[^a-zA-Z0-9-_]/g, "").slice(0, 128);
};

async function getServerToken(cookieStore) {
  let token = null;
  let tokenExpiryDate = null;
  const identifier = cookieStore.get("identifier");
  const fetchToken = cookieStore.get("token");
  const fetchTokenExpiryDate = cookieStore.get("tokenExpiryDate");
  if (fetchToken && fetchToken.value) {
    token = fetchToken.value;
  }

  if (fetchTokenExpiryDate && fetchTokenExpiryDate.value) {
    tokenExpiryDate = fetchTokenExpiryDate.value;
  }

  if (!token || !tokenExpiryDate) {
    return getNewToken(identifier);
  }
  tokenExpiryDate = new Date(tokenExpiryDate);
  const currentDate = new Date();
  if (tokenExpiryDate < currentDate) {
    return getNewToken(identifier);
  }
  return token;
}

export async function fetchHandleInfo(cookieStore, handle, location, checkSum) {
  if (!handle || handle === "undefined") return null;
  const safeHandle = encodeURIComponent(handle);
  const safeCheckSum = checkSum ? encodeURIComponent(checkSum) : "";

  const token = await getServerToken(cookieStore);
  if (!token) return null;

  const fetchData = async (url) => {
    try {
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        next: { revalidate: 10 },
      });
      return res.ok ? await res.json() : null;
    } catch (err) {
      console.error("Fetch error:", err);
      return null;
    }
  };

  const handleRes = await fetchData(
    `${CONFIG.apiUrl}/handle/public/${safeHandle}/info`,
  );
  const handleData = handleRes?.data?.handle;

  if (!handleData) {
    console.log("could not get handle data", {handleRes});
    return null;
  }

  if (handleData.profileType === "INDIVIDUAL") return null;

  return fetchProfileCommon({
    token,
    profileId: handleData.profileId,
    location,
    checkSum: safeCheckSum,
  });
}

export async function fetchDomainInfo(cookieStore, domain, location, checkSum) {
  if (!domain || domain === "undefined") return null;
  const safeDomain = encodeURIComponent(domain);
  const safeCheckSum = checkSum ? encodeURIComponent(checkSum) : "";

  const token = await getServerToken(cookieStore);
  if (!token) return null;

  const fetchData = async (url) => {
    try {
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        next: { revalidate: 10 },
      });
      return res.ok ? await res.json() : null;
    } catch (err) {
      console.error("Fetch error:", err);
      return null;
    }
  };

  const domainRes = await fetchData(
    `${CONFIG.apiUrl}/domain/${safeDomain}/info`,
  );
  const domainData = domainRes?.data?.domain;

  if (!domainData) {
    console.log("could not get domain data", { domain });
    return null;
  }

  if (domainData.status !== "CONNECTED") return null;

  return fetchProfileCommon({
    token,
    profileId: domainData.profileId,
    location,
    checkSum: safeCheckSum,
  });
}

async function fetchProfileCommon({ token, profileId, location, checkSum }) {
  const fetchData = async (url) => {
    try {
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        next: { revalidate: 10 },
      });
      return res.ok ? await res.json() : null;
    } catch (err) {
      console.error("Fetch error:", err);
      return null;
    }
  };

  // --- Organisation info ---
  const orgRes = await fetchData(
    `${CONFIG.apiUrl}/organisation/public/${profileId}/info`,
  );
  const org = orgRes?.data;

  if (!org) {
    console.log("could not get organisation information");
    return null;
  }

  const settings = org?.organisation?.settings || {};
  if (!settings.enableOnlineStore || !settings.storefrontPublic) {
    console.log("Storefront disabled");
    return null;
  }

  // --- Access Points ---
  const accessPointRes = await fetchData(
    `${CONFIG.apiUrl}/crowdcontrol/organisation/${profileId}/accesspoints/inventory`,
  );
  const accessPoints = accessPointRes?.data?.accessPoints || [];

  if (accessPoints.length === 0) {
    console.log("No access point found");
    return null;
  }

  // Pick access point
  let accessPointId;
  if (location) {
    const normalizedLocation = location.replace(/-/g, " ").toLowerCase();
    const match = accessPoints.find(
      (ap) => ap.name.toLowerCase() === normalizedLocation,
    );
    accessPointId = match?.accessPointId || accessPoints[0].accessPointId;
  } else {
    accessPointId = accessPoints[0].accessPointId;
  }

  const accessName = formatUrlName(
    accessPoints.find((ap) => ap.accessPointId === accessPointId)?.name || "",
  );

  // --- Product by checksum ---
  let urlData = null;
  if (checkSum) {
    const productRes = await fetchData(
      `${CONFIG.apiUrl}/product/public/organisation/${profileId}/checksum/${checkSum}/info?useFeed=true&accessPointId=${accessPointId}`,
    );
    urlData = productRes?.data?.product || null;
  }

  // --- Categories + Cart ---
  const [categoriesRes, cartRes] = await Promise.all([
    fetchData(
      `${CONFIG.apiUrl}/product/public/category/organisation/${profileId}/feeds?accessPointId=${accessPointId}`,
    ),
    fetchData(
      `${CONFIG.apiUrl}/collection/organisation/${profileId}/cart?accessPointId=${accessPointId}`,
    ),
  ]);

  const categories = categoriesRes?.data?.productCategories || [];
  const cart = cartRes?.data?.collection || null;

  return {
    org,
    categories,
    accessPoints,
    token,
    accessPointId,
    accessName,
    cart,
    urlData,
    profileId,
  };
}

export function isValidPath(first, knownPaths, accessPoints) {
  const lowerKnownPaths = knownPaths.map((p) => p.toLowerCase());
  const isKnownPath = first ? lowerKnownPaths.includes(first) : true;
  const isAccessPoint = first
    ? accessPoints.some(
        (ap) => ap.name.replace(/\s+/g, "-").toLowerCase() === first,
      )
    : true;

  return isKnownPath || isAccessPoint;
}

export async function resolvePage({
  classification,
  domainInfo,
  cookieStore,
  params,
}) {
  console.log(JSON.stringify({ classification, domainInfo, params }));
  const rawSlug = Array.isArray(params.slug) ? params.slug : [];
  const slug = rawSlug
    .map((seg) => sanitizePathSegment(seg))
    .filter((seg) => seg.length > 0);
  const identifier =
    classification.type === "store" ? classification.handle : domainInfo.domain;
  const {
    urlPath,
    location,
    productNo,
    fullUrlPath,
    knownPaths,
    firstSegment,
  } = parseStoreSlug(identifier, slug);

  const fetchFn =
    classification.type === "store" ? fetchHandleInfo : fetchDomainInfo;

  const res = await fetchFn(cookieStore, identifier, location, productNo);

  return {
    res,
    urlPath,
    location,
    productNo,
    fullUrlPath,
    knownPaths,
    firstSegment,
  };
}

export async function fetchOrganisations(cookieStore) {
  const token = await getServerToken(cookieStore);
  if (!token) {
    console.log("count not get server token");
    return null;
  }

  const fetchData = async (url) => {
    try {
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        next: { revalidate: 10 },
      });
      return res.ok ? await res.json() : null;
    } catch (err) {
      console.error("Fetch error:", err);
      return null;
    }
  };

  try {
    const OrgRes = await fetchData(
      `${CONFIG.apiUrl}organisation/organisations/storefront/enabled?page=1&limit=10&searchTerm=phone`,
    );
    const urlData = OrgRes?.data || [];

    return {
      urlData,
      token,
    };
  } catch (err) {
    console.error("fetchHandleInfo error:", err);
    return null;
  }
}

/**
 * Parse slug into urlPath, location, productNo, fullUrlPath, firstSegment, and lastSegment
 * @param {string} handle - the store handle
 * @param {string[]|string} slugParam - the slug array or string
 */
export const parseStoreSlug = (handle, slugParam) => {
  const slug = Array.isArray(slugParam) ? slugParam : [];
  const fullUrlPath = slug.join("/");

  // detect urlPath (home, product, auth, etc.)
  const urlPath = getUrlPathFromPathname(`/store/${handle}/${fullUrlPath}`);

  // detect location (first segment if not a known path)
  const guessLocation = slug[0];
  const location =
    guessLocation &&
    !knownPaths.some((p) => p.toLowerCase() === guessLocation.toLowerCase())
      ? guessLocation
      : undefined;

  // detect product number from slug if urlPath = product
  let productNo = null;
  if (urlPath === "product") {
    const productIndex = slug.indexOf("product");
    const productSegment = slug[productIndex + 1];
    if (productSegment) {
      const parts = productSegment.split("-");
      productNo = parts[parts.length - 1];
    }
  }

  // ✅ first and last segments
  const firstSegment = slug.length > 0 ? slug[0] : undefined;
  const lastSegment = slug.length > 0 ? slug[slug.length - 1] : undefined;

  return {
    urlPath,
    location,
    productNo,
    fullUrlPath,
    firstSegment,
    lastSegment,
    knownPaths,
  };
};

export const parseMarketplaceSlug = (slugParam = []) => {
  const slug = Array.isArray(slugParam) ? slugParam : [];
  const fullUrlPath = slug.join("/");

  const firstSegment = slug[0]?.toLowerCase();

  const urlPath = firstSegment ?? "market-place";

  const isKnownPath =
    !firstSegment || knownMarketplacePaths.includes(firstSegment);

  return {
    urlPath,
    fullUrlPath,
    firstSegment,
    isKnownPath,
    knownMarketplacePaths,
  };
};

export function getDomainObject(headersList) {
  // Prefer forwarded headers since we often sit behind a proxy/load balancer
  const forwardedProto = headersList.get("x-forwarded-proto");
  const forwardedHost = headersList.get("x-forwarded-host");

  const protocol = (forwardedProto || "http").split(",")[0].trim();
  const rawHost = forwardedHost || headersList.get("host") || "localhost:3000";

  // Use the first host if multiple are provided (comma-separated)
  const normalizedHost = rawHost.split(",")[0].trim().toLowerCase();
  // Remove anything outside the allowed host characters
  const sanitizedHost = normalizedHost.replace(/[^a-z0-9._:-]/g, "");
  const host = sanitizedHost || "localhost:3000";
  return {
    fullDomain: `${protocol}://${host}`,
    domain: host,
  };
}

export function classifyDomain(domainInfo) {
  const { domain } = domainInfo;
  const cleanDomain = domain.split(":")[0].toLowerCase();

  // ✅ Home domains
  if (
    cleanDomain === "circleplug.com" ||
    cleanDomain === "stage.circleplug.com" ||
    cleanDomain === "www.circleplug.com" ||
    cleanDomain === "localhost"
  ) {
    const env =
      cleanDomain === "localhost"
        ? "local"
        : cleanDomain.includes("stage.")
          ? "stage"
          : "prod";
    return { type: "home", env };
  }

  if (
    cleanDomain === "circleplug.shop" ||
    cleanDomain === "stage.circleplug.shop" ||
    cleanDomain === "www.circleplug.shop"
  ) {
    // "stage.circleplug.shop" is treated as home but with stage env
    const env = cleanDomain.includes("stage.") ? "stage" : "prod";
    return { type: "home", env };
  }

  // ✅ Subdomain stores (stage first so it doesn't fall through)
  if (cleanDomain.endsWith(".stage.circleplug.shop")) {
    const subdomain = cleanDomain.replace(".stage.circleplug.shop", "");
    return { type: "store", handle: subdomain, env: "stage" };
  }

  if (cleanDomain.endsWith(".circleplug.shop")) {
    const subdomain = cleanDomain.replace(".circleplug.shop", "");
    return { type: "store", handle: subdomain, env: "prod" };
  }

  // ✅ External domainss
  return { type: "external", domain: cleanDomain };
}

export function generateHomePageMetadata() {
  const title =
    "CirclePlug | Everything your community needs, all in one place";
  const description =
    "Forget juggling multiple platforms! Build, engage, and monetize your thriving community – all within one seamless platform.";

  return {
    title,
    description,
    openGraph: {
      title,
      description,
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
    },
  };
}

export function generateStoreMetadata(data) {
  const orgName = data.orgName || "Store";
  const category = data.category || "Business";
  const country = data.country || "";
  const sector = data.sector || "services";
  const description =
    data.description ||
    `${orgName} offers a range of services in the ${sector} sector. Join us to experience top-notch services and stay updated.`;

  const openGraphImages = [];
  if (data.logoUrl) {
    openGraphImages.push({
      url: data.logoUrl,
      width: 800,
      height: 600,
      alt: `${orgName} Logo`,
    });
  }
  if (data.coverPhoto) {
    openGraphImages.push({
      url: data.coverPhoto,
      width: 1200,
      height: 630,
      alt: `${orgName} Cover Photo`,
    });
  }

  return {
    title: `${orgName} | ${category}${country ? ` in ${country}` : ""}`,
    description,
    openGraph: {
      title: `${orgName} - ${category}`,
      description,
      url: data.webLink || "",
      site_name: orgName,
      ...(openGraphImages.length && { images: openGraphImages }),
    },
    twitter: {
      card: "summary_large_image",
      title: `${orgName} - ${category}`,
      description,
      ...(data.logoUrl && { images: [data.logoUrl] }),
    },
  };
}

export function generateProductMetadata(product, org) {
  const productName = product.name || "Product";
  const orgName = org.orgName || "Store";
  const description =
    product.description?.trim() ||
    `Buy ${productName} from ${orgName} online. Enjoy great quality at an affordable price.`;

  const images = (product.media || []).filter(
    (media) => media.mediaType === "image",
  );
  const mainImage = images[0]?.mediaThumb || org.logoUrl || "";

  const meta = {
    title: `${productName} | ${orgName}`,
    description,
    openGraph: {
      title: `${productName} - ${orgName}`,
      description,
      ...(mainImage && {
        images: [
          {
            url: mainImage,
            width: 800,
            height: 600,
            alt: productName,
          },
        ],
      }),
    },
    twitter: {
      card: "summary_large_image",
      title: `${productName} - ${orgName}`,
      description,
      ...(mainImage && { images: [mainImage] }),
    },
  };

  return meta;
}
